// Ported from genieos/web/src/components/historian/tableFormat.ts @ 84923d036
/**
 * Cell formatting for the historian results table.
 *
 * WHY A SEPARATE MODULE: `react-refresh/only-export-components` (a warning,
 * under `--max-warnings 0`) fails lint if a component file exports a plain
 * function — and these two are worth testing directly, because both defects
 * they fix are invisible at the interval the table could previously reach.
 */

/**
 * Significant digits kept for a non-integer cell value.
 *
 * ⚠️ The table rendered `value.toFixed(2)` until Demsv1.0#3996. At the intervals
 * the UI could reach that was merely lossy; with the raw interval it is
 * disqualifying — the synchrophasor stream measured at Turner-001 on 2026-09-06
 * resolves ~1 mHz (60.026, 60.028, 60.020, 60.011, 60.006), and two decimal
 * places renders all five as "60.03"/"60.02"/"60.01". A flat column, in the one
 * column the operator opened the page for.
 *
 * Seven covers a TDengine FLOAT's ~7 decimal digits and every scaled-integer
 * device register in the fleet, without widening an averaged kW column into
 * float noise.
 */
const SIGNIFICANT_DIGITS = 7;

/**
 * Formats one non-timestamp cell.
 *
 * Integers pass through untouched: `toPrecision` would round a 9-digit counter
 * or a bitfield to significant digits (123456789 -> 123456800) and print `0` as
 * "0.000000". Non-integers are rounded to SIGNIFICANT_DIGITS and re-parsed,
 * which drops the trailing zeros `toPrecision` pads with — so 476.5 stays
 * "476.5" rather than becoming "476.5000".
 */
export function formatHistorianValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return String(value);
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  // Never round the INTEGER part away. `toPrecision(7)` on 123456789.5 switches
  // to exponential and re-parses as 123456800 — the whole-number part wrong by
  // 11, which is the same defect the integer branch above exists to avoid, just
  // one step further out. Reachable: the browser exposes the full live column
  // list for every device, so an energy accumulator can land in this table.
  // Widening to the magnitude keeps the integer digits and only ever drops
  // sub-unit precision that seven significant digits could not have shown
  // anyway. Capped at 15 — a double carries no more than ~15-17 honest digits,
  // and past that toPrecision would print noise.
  const magnitudeDigits = Math.floor(Math.log10(Math.abs(value))) + 1;
  const digits = Math.min(15, Math.max(SIGNIFICANT_DIGITS, magnitudeDigits));
  return String(parseFloat(value.toPrecision(digits)));
}

/**
 * Builds a timestamp formatter for the results table.
 *
 * `withMillis` should be set for a raw result and only for a raw result. At
 * 60 fps `toLocaleString()` renders sixty consecutive rows as the same
 * "10:56:25 AM", which makes the column non-discriminating exactly where the
 * time is the measurement; on an hourly trend, ".000" on every row is noise.
 *
 * The formatter is built ONCE and reused. Constructing an Intl.DateTimeFormat
 * per cell is the expensive half of formatting, and a raw result is ~18,000
 * rows.
 *
 * ⚠️ Milliseconds are the finest resolution available here, not the finest the
 * PMU produced — the API floors row timestamps to whole milliseconds
 * (Demsv1.0#3997).
 */
export function makeHistorianTimestampFormatter(
  withMillis: boolean
): (ts: number) => string {
  const formatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    ...(withMillis ? { fractionalSecondDigits: 3 as const } : {}),
  });
  return (ts: number) => formatter.format(new Date(ts));
}
